/**
 * Reading a prescribed medicine line out of `/get_ipd_prescriptions/`.
 *
 * Import-free so the quantity maths is unit-testable (pharmacyMappers reaches axios
 * through api/inventory-request and cannot be loaded under Jest).
 *
 * The endpoint ships medicine rows in two shapes, because two writers create them:
 *
 *   simple  details: { dose: "M:1", duration: "10 Day(s)", frequency: "STAT" }
 *           details: { route, duration: 15, frequency, instructions, duration_unit }
 *
 *   CPOE    details: { dose: "", stat, morning, afternoon, evening, night, route,
 *                      instructions, duration, duration_days, frequency_code,
 *                      priority, total_quantity, drug_name, generic_name }
 *
 * Everything clinical lives inside `details` in the CPOE shape, never on the medicine
 * row itself — so a resolver that only reads the row silently loses it.
 */

/** Per-slot dose counts, in administration order. Labels match the simple shape's own "M:1" notation. */
const DOSE_SLOTS = [
  { key: 'morning', label: 'M' },
  { key: 'afternoon', label: 'A' },
  { key: 'evening', label: 'E' },
  { key: 'night', label: 'N' },
];

export function medicineDetails(row) {
  return row?.details && typeof row.details === 'object' ? row.details : {};
}

/** First value that is neither nullish nor an empty string. `0` and `false` count as present. */
function firstFilled(...values) {
  for (const value of values) {
    if (value != null && value !== '') return value;
  }
  return '';
}

/** First value that parses to a number greater than zero. */
function firstPositive(...values) {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) return num;
  }
  return 0;
}

function slotCount(details, key) {
  const num = Number(details?.[key]);
  return Number.isFinite(num) ? num : 0;
}

/** Leading integer of "10 Day(s)" / "15 days" / 15 → 10 / 15 / 15. */
export function parseDurationDays(duration) {
  const match = String(duration ?? '').match(/(\d+)/);
  return match ? Number.parseInt(match[1], 10) : 0;
}

/** Keys that would carry a real prescribed quantity, checked on the row then in `details`. */
const QTY_KEYS = ['total_quantity', 'prescribed_qty', 'prescribed_quantity', 'quantity', 'qty'];

/**
 * The quantity the prescriber actually stated — 0 when they stated none.
 *
 * `total_quantity` lives in `details` on CPOE orders, so reading only the row fell
 * through to the duration estimate: an order for 5 vials over 15 days was offered
 * for dispense as 15.
 *
 * Zero is a real answer here, not a failure. CPOE ships `total_quantity: 0` meaning
 * "never computed", and the caller has to be able to tell that apart from a genuine
 * number — see resolvePrescribedQty, which estimates, and the dispense screen, which
 * refuses to pre-fill an estimate as though a doctor had written it.
 */
export function explicitPrescribedQty(row) {
  const details = medicineDetails(row);
  return firstPositive(
    ...QTY_KEYS.map((key) => row?.[key]),
    ...QTY_KEYS.map((key) => details[key]),
  );
}

/**
 * Best available quantity: what was prescribed, else an estimate from the duration,
 * else 1.
 *
 * The estimate exists so a line with no stated quantity still appears on the dispense
 * screen (lines are dropped when this is 0) — NOT so it can be dispensed as-is. Any
 * caller putting this number in front of a pharmacist must check
 * `explicitPrescribedQty` first and show that it was estimated.
 */
export function resolvePrescribedQty(row) {
  const details = medicineDetails(row);
  const explicit = explicitPrescribedQty(row);
  if (explicit > 0) return explicit;

  const days = parseDurationDays(firstFilled(
    row?.duration,
    row?.number_of_days,
    details.duration,
    details.duration_days,
    details.number_of_days,
  ));
  return days > 0 ? days : 1;
}

/**
 * Dose text for display.
 *
 * Simple orders ship a ready string ("M:1"). CPOE orders ship `dose: ""` plus the
 * per-slot counts, which rendered as an empty dose cell on the dispense screen even
 * though the schedule was right there. Derived output uses the same notation the
 * simple shape already uses, so both look alike in the table.
 */
export function resolveDoseText(row) {
  const details = medicineDetails(row);
  const explicit = firstFilled(
    row?.dose,
    row?.quantity_per_dose,
    row?.dosage,
    details.dose,
    details.dosage,
    row?.dosage_form,
  );
  if (explicit) return String(explicit);

  return DOSE_SLOTS
    .filter((slot) => slotCount(details, slot.key) > 0)
    .map((slot) => `${slot.label}:${slotCount(details, slot.key)}`)
    .join(' ');
}

/**
 * Duration text. The older order form sends a bare number with the unit in a sibling
 * `duration_unit`, which rendered as an unlabelled "15" beside the frequency.
 */
export function resolveDurationText(row) {
  const details = medicineDetails(row);
  const raw = firstFilled(row?.duration, row?.number_of_days, details.duration, details.number_of_days);
  const unit = firstFilled(row?.duration_unit, details.duration_unit);

  if (raw !== '') {
    const text = String(raw).trim();
    return unit && /^\d+$/.test(text) ? `${text} ${unit}` : text;
  }

  const days = parseDurationDays(details.duration_days);
  return days > 0 ? `${days} day${days === 1 ? '' : 's'}` : '';
}

/** Administration route — CPOE keeps it in `details`, older shapes on the row. */
export function resolveRoute(row) {
  const details = medicineDetails(row);
  return String(firstFilled(
    row?.dosage_form,
    row?.route,
    row?.administration_route,
    details.route,
    details.dosage_form,
    details.administration_route,
  ));
}

/**
 * Clinical instructions ("infuse over 15-30 minutes per protocol").
 *
 * The medicine-name fallback is kept last for payload shapes that carry no notes at
 * all, but real instructions from `details` now win — previously the name always did,
 * which both hid the instruction and sent the drug name through as dispense remarks.
 */
export function resolveInstructions(row) {
  const details = medicineDetails(row);
  return String(firstFilled(
    row?.notes,
    row?.instructions,
    details.instructions,
    details.notes,
    row?.medicine_name,
    row?.name,
  ));
}
